import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useContracts, useClients, useTarifas, useComisionConfig, useConvenios } from "../../contexts/DataContext";
import { useUser } from "../../components/UserContext";
import { Heading } from "../../components/heading";
import { Text } from "../../components/text";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/table";
import { Badge } from "../../components/badge";
import { InformationCircleIcon, ChevronDownIcon, XMarkIcon } from "@heroicons/react/20/solid";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { FianzaView } from "../../components/FianzaView";
import {
  getFechaEmision,
  getEmisorNombre,
  getSubRamo,
  getTipoFianza,
  formatNumeroFianza,
} from "../../utils/calculations";
import { useComisionLedger } from "../../hooks/useComisionLedger";
import { indexMovementsByFianza, resolveComision } from "../../utils/comisionReconcile";

const UNASSIGNED_KEY = "__unassigned__";

const monthNames = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

// Avatar color based on UID hash - matching AssignedUsersBubbles
const AVATAR_COLORS = [
  'bg-azul-surelia/20 text-azul-surelia',
  'bg-verde-lima/30 text-azul-surelia',
  'bg-cian-digital/20 text-cian-digital',
  'bg-purple-100 text-purple-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
  'bg-teal-100 text-teal-700',
  'bg-indigo-100 text-indigo-700',
];

// Line colors for chart - matching avatar colors
const LINE_COLORS = [
  '#284865', // azul-surelia
  '#C2F40C', // verde-lima
  '#00D4FF', // cian-digital
  '#7c3aed', // purple
  '#f59e0b', // amber
  '#f43f5e', // rose
  '#14b8a6', // teal
  '#6366f1', // indigo
];

const getAvatarColor = (uid) => {
  if (!uid || uid === UNASSIGNED_KEY) return 'bg-gray-100 text-gray-500';
  const hash = uid.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

const getLineColor = (uid) => {
  if (!uid || uid === UNASSIGNED_KEY) return '#9ca3af'; // gray
  const hash = uid.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return LINE_COLORS[hash % LINE_COLORS.length];
};

export function ProduccionPorResponsable({ selectedYear }) {
  const { contractsList, contractsLoading } = useContracts();
  const { clientsList, clientsById, clientsLoading } = useClients();
  const { tarifas, tarifasLoading } = useTarifas();
  const { movements } = useComisionLedger();
  const { config: comisionConfig } = useComisionConfig();
  const { conveniosById } = useConvenios();
  const byFianza = useMemo(() => indexMovementsByFianza(movements), [movements]);
  const { getUserName, getInitials } = useUser();

  const [selectedMonth, setSelectedMonth] = useState("all");
  const [attributionMode, setAttributionMode] = useState("individual"); // "individual" | "compartido"
  const [showTooltip, setShowTooltip] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [slideOverData, setSlideOverData] = useState(null); // { responsableId, monthIndex }
  const [selectedFianza, setSelectedFianza] = useState(null); // For FianzaView

  // Build fianzas with all needed data
  const allFianzas = useMemo(() => {
    return contractsList.flatMap((contract) => {
      // Convenio-split context so the on-the-fly fallback nets the same way
      // the ledger books (see comisionReconcile.resolveComision).
      const splitOpts = {
        contractData: contract,
        orgConfig: comisionConfig,
        convenioData: contract.convenioId ? conveniosById?.[contract.convenioId] : null,
      };
      const client = clientsById[contract.clientId];
      const clientName = client?.nombreEmpresa || "Cliente sin nombre";

      // Get responsables: combine client + contract assignedUsers
      const clientResponsables = client?.assignedUsers || [];
      const contractResponsables = contract.assignedUsers || [];
      const allResponsables = [...new Set([...clientResponsables, ...contractResponsables])];

      return (contract.fianzas || [])
        .filter((fianza) => fianza.estadoFianza !== 'anulada' && fianza.estadoFianza !== 'sustituida') // anulada + sustituida excluded; cancelada retained
        .map((fianza) => {
        const inicio = getFechaEmision(fianza);
        const montoAfianzado = Number(fianza.montoAfianzado) || 0;
        const emisorNombre = getEmisorNombre(fianza);
        const fianzaSubRamo = getSubRamo(fianza);
        const fianzaTipo = getTipoFianza(fianza);

        // Ledger-net prima AND comisión when the fianza has movements; on-the-fly
        // otherwise — so the prima column matches the comisión beside it.
        const {
          prima: primaAmount,
          comisionPct,
          comisionMonto,
          primaMinima,
          tarifa: fianzaTarifa,
        } = resolveComision(
          fianza, byFianza.get(fianza.id), tarifas, emisorNombre, fianzaSubRamo, fianzaTipo, splitOpts
        );

        return {
          ...fianza,
          clientId: contract.clientId,
          contratoId: contract.id,
          clientName,
          inicioVigenciaMs: inicio,
          montoAfianzado,
          primaAmount,
          fianzaTarifa,
          primaMinima,
          comisionPct,
          comisionMonto,
          responsables: allResponsables.length > 0 ? allResponsables : [UNASSIGNED_KEY],
          responsablesCount: allResponsables.length > 0 ? allResponsables.length : 1,
        };
      });
    });
  }, [contractsList, clientsById, tarifas, byFianza, comisionConfig, conveniosById]);

  // Get unique clients with their responsables
  const clientsWithResponsables = useMemo(() => {
    return clientsList.map((client) => ({
      id: client.id,
      responsables: client.assignedUsers?.length > 0 ? client.assignedUsers : [UNASSIGNED_KEY],
      responsablesCount: client.assignedUsers?.length > 0 ? client.assignedUsers.length : 1,
    }));
  }, [clientsList]);

  // Get unique contracts with their responsables
  const contractsWithResponsables = useMemo(() => {
    return contractsList.map((contract) => {
      const client = clientsById[contract.clientId];
      const clientResponsables = client?.assignedUsers || [];
      const contractResponsables = contract.assignedUsers || [];
      const allResponsables = [...new Set([...clientResponsables, ...contractResponsables])];
      return {
        id: contract.id,
        responsables: allResponsables.length > 0 ? allResponsables : [UNASSIGNED_KEY],
        responsablesCount: allResponsables.length > 0 ? allResponsables.length : 1,
      };
    });
  }, [contractsList, clientsById]);

  // Filter fianzas by period
  const filteredFianzas = useMemo(() => {
    return allFianzas.filter((f) => {
      if (!f.inicioVigenciaMs) return false;
      const date = new Date(f.inicioVigenciaMs);
      const yr = date.getFullYear();
      const month = date.getMonth();
      if (yr !== selectedYear) return false;
      if (selectedMonth !== "all" && month !== selectedMonth) return false;
      return true;
    });
  }, [allFianzas, selectedYear, selectedMonth]);

  // Aggregate by responsable
  const resumenPorResponsable = useMemo(() => {
    const map = new Map();

    // Initialize with all responsables from clients
    const allUserIds = new Set();
    clientsWithResponsables.forEach((c) => c.responsables.forEach((uid) => allUserIds.add(uid)));

    allUserIds.forEach((uid) => {
      map.set(uid, {
        id: uid,
        clientesSet: new Set(),
        contratosSet: new Set(),
        fianzasCount: 0,
        prima: 0,
        comision: 0,
      });
    });

    // Count clients per responsable
    clientsWithResponsables.forEach((client) => {
      client.responsables.forEach((uid) => {
        const entry = map.get(uid);
        if (entry) {
          entry.clientesSet.add(client.id);
        }
      });
    });

    // Count contracts per responsable
    contractsWithResponsables.forEach((contract) => {
      contract.responsables.forEach((uid) => {
        const entry = map.get(uid);
        if (entry) {
          entry.contratosSet.add(contract.id);
        }
      });
    });

    // Aggregate fianzas values
    filteredFianzas.forEach((fianza) => {
      const divisor = attributionMode === "compartido" ? fianza.responsablesCount : 1;
      fianza.responsables.forEach((uid) => {
        let entry = map.get(uid);
        if (!entry) {
          entry = {
            id: uid,
            clientesSet: new Set(),
            contratosSet: new Set(),
            fianzasCount: 0,
            prima: 0,
            comision: 0,
          };
          map.set(uid, entry);
        }
        entry.fianzasCount += 1 / divisor;
        entry.prima += (fianza.primaAmount || 0) / divisor;
        entry.comision += (fianza.comisionMonto || 0) / divisor;
      });
    });

    // Convert to array and sort by prima descending, but always put unassigned last
    return Array.from(map.values())
      .map((entry) => ({
        ...entry,
        clientes: entry.clientesSet.size,
        contratos: entry.contratosSet.size,
        fianzas: Math.round(entry.fianzasCount * 100) / 100, // Round for display
      }))
      .sort((a, b) => {
        // Always put unassigned last
        if (a.id === UNASSIGNED_KEY) return 1;
        if (b.id === UNASSIGNED_KEY) return -1;
        return b.prima - a.prima;
      });
  }, [clientsWithResponsables, contractsWithResponsables, filteredFianzas, attributionMode]);

  // Monthly breakdown by responsable (for the year)
  const monthlyByResponsable = useMemo(() => {
    // Filter fianzas for selected year only
    const yearFianzas = allFianzas.filter((f) => {
      if (!f.inicioVigenciaMs) return false;
      return new Date(f.inicioVigenciaMs).getFullYear() === selectedYear;
    });

    // Get all unique responsables
    const allUserIds = new Set();
    clientsWithResponsables.forEach((c) => c.responsables.forEach((uid) => allUserIds.add(uid)));
    yearFianzas.forEach((f) => f.responsables.forEach((uid) => allUserIds.add(uid)));

    // Build matrix: responsable -> month -> { prima, comision, count, clientesSet, contratosSet }
    const matrix = new Map();
    allUserIds.forEach((uid) => {
      matrix.set(uid, {
        id: uid,
        months: Array.from({ length: 12 }, () => ({
          prima: 0,
          comision: 0,
          count: 0,
          clientesSet: new Set(),
          contratosSet: new Set(),
        })),
        total: { prima: 0, comision: 0, count: 0 },
      });
    });

    yearFianzas.forEach((fianza) => {
      const month = new Date(fianza.inicioVigenciaMs).getMonth();
      const divisor = attributionMode === "compartido" ? fianza.responsablesCount : 1;

      fianza.responsables.forEach((uid) => {
        const entry = matrix.get(uid);
        if (entry) {
          entry.months[month].prima += (fianza.primaAmount || 0) / divisor;
          entry.months[month].comision += (fianza.comisionMonto || 0) / divisor;
          entry.months[month].count += 1 / divisor;
          entry.months[month].clientesSet.add(fianza.clientId);
          entry.months[month].contratosSet.add(fianza.contratoId);
          entry.total.prima += (fianza.primaAmount || 0) / divisor;
          entry.total.comision += (fianza.comisionMonto || 0) / divisor;
          entry.total.count += 1 / divisor;
        }
      });
    });

    // Convert sets to counts for the final output
    return Array.from(matrix.values())
      .map((entry) => ({
        ...entry,
        months: entry.months.map((m) => ({
          prima: m.prima,
          comision: m.comision,
          count: m.count,
          clientes: m.clientesSet.size,
          contratos: m.contratosSet.size,
        })),
      }))
      .sort((a, b) => {
        // Always put unassigned last
        if (a.id === UNASSIGNED_KEY) return 1;
        if (b.id === UNASSIGNED_KEY) return -1;
        return b.total.prima - a.total.prima;
      });
  }, [allFianzas, selectedYear, clientsWithResponsables, attributionMode]);

  // Get monthly data for a specific responsable
  const getMonthlyDataForResponsable = (responsableId) => {
    return monthlyByResponsable.find((r) => r.id === responsableId)?.months || [];
  };

  // Get fianzas for a specific responsable and month
  const getFianzasForResponsableMonth = (responsableId, monthIndex) => {
    return allFianzas.filter((f) => {
      if (!f.inicioVigenciaMs) return false;
      const date = new Date(f.inicioVigenciaMs);
      if (date.getFullYear() !== selectedYear) return false;
      if (date.getMonth() !== monthIndex) return false;
      return f.responsables.includes(responsableId);
    });
  };

  // Get client and contract data for a fianza (for FianzaView)
  const getClientDataForFianza = (fianza) => {
    return clientsById[fianza.clientId] || {};
  };

  const getContractDataForFianza = (fianza) => {
    return contractsList.find((c) => c.id === fianza.contratoId) || {};
  };

  const toggleRowExpanded = (id) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Chart data - transform monthly data for recharts
  const chartData = useMemo(() => {
    return monthNames.map((month, monthIndex) => {
      const dataPoint = {
        month: month.substring(0, 3).charAt(0).toUpperCase() + month.substring(1, 3),
      };
      monthlyByResponsable.forEach((resp) => {
        const name = resp.id === UNASSIGNED_KEY ? "Sin asignar" : (getUserName(resp.id) || "Usuario");
        dataPoint[name] = resp.months[monthIndex].prima;
      });
      return dataPoint;
    });
  }, [monthlyByResponsable, getUserName]);

  const loading = contractsLoading || clientsLoading || tarifasLoading;

  const formatCurrency = (value) =>
    new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(value);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-3">
          <Heading level={1} className="text-3xl font-bold text-azul-surelia">
            Producción por Responsable
          </Heading>
          <Badge color="blue">{selectedYear}</Badge>
        </div>
        <Text className="text-azul-surelia/70">
          Producción y comisiones estimadas por miembro del equipo en el periodo seleccionado.
        </Text>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center" data-pdf-hide>
        <div className="flex items-center gap-2">
          <Text className="text-sm text-azul-surelia/80">Mes</Text>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value === "all" ? "all" : Number(e.target.value))}
            className="text-sm border rounded-lg px-3 py-2"
          >
            <option value="all">Todo el año</option>
            {monthNames.map((name, idx) => (
              <option key={name} value={idx}>
                {name.charAt(0).toUpperCase() + name.slice(1)}
              </option>
            ))}
          </select>
        </div>

        {/* Attribution mode toggle */}
        <div className="flex items-center gap-2 ml-auto">
          <div className="relative">
            <button
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
              className="text-azul-surelia/60 hover:text-azul-surelia"
            >
              <InformationCircleIcon className="w-5 h-5" />
            </button>
            {showTooltip && (
              <div className="absolute right-0 top-7 z-50 w-72 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-xl">
                <p className="font-semibold mb-2">Modo de atribución:</p>
                <p className="mb-2">
                  <span className="font-medium">Individual:</span> Cada responsable ve el 100% de los valores de sus clientes/contratos. Los totales pueden estar duplicados si hay múltiples responsables.
                </p>
                <p>
                  <span className="font-medium">Compartido:</span> Los valores se dividen equitativamente entre los responsables asignados. Los totales reflejan la suma real sin duplicados.
                </p>
              </div>
            )}
          </div>
          <div className="flex rounded-lg border border-azul-surelia/30 overflow-hidden">
            <button
              onClick={() => setAttributionMode("individual")}
              className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                attributionMode === "individual"
                  ? "bg-azul-surelia text-white"
                  : "bg-white text-azul-surelia hover:bg-azul-surelia/5"
              }`}
            >
              Individual
            </button>
            <button
              onClick={() => setAttributionMode("compartido")}
              className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                attributionMode === "compartido"
                  ? "bg-azul-surelia text-white"
                  : "bg-white text-azul-surelia hover:bg-azul-surelia/5"
              }`}
            >
              Compartido
            </button>
          </div>
        </div>
      </div>

      {/* Production Chart */}
      {!loading && monthlyByResponsable.length > 0 && (
        <div className="bg-white border border-azul-surelia/15 rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-zinc-200">
            <Heading level={3} className="text-xl text-azul-surelia">
              Producción mensual
            </Heading>
            <Text className="text-sm text-azul-surelia/60 mt-1">
              Prima por responsable durante {selectedYear}
            </Text>
          </div>
          <div className="p-4">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="month"
                  tick={{ fill: '#284865', fontSize: 12 }}
                  axisLine={{ stroke: '#d1d5db' }}
                />
                <YAxis
                  tickFormatter={(value) =>
                    new Intl.NumberFormat("es-MX", {
                      style: "currency",
                      currency: "MXN",
                      notation: "compact",
                      maximumFractionDigits: 1,
                    }).format(value)
                  }
                  tick={{ fill: '#284865', fontSize: 12 }}
                  axisLine={{ stroke: '#d1d5db' }}
                />
                <Tooltip
                  formatter={(value) => formatCurrency(value)}
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                  }}
                  labelStyle={{ color: '#284865', fontWeight: 600 }}
                />
                <Legend
                  wrapperStyle={{ paddingTop: '20px' }}
                  iconType="circle"
                />
                {monthlyByResponsable.map((resp) => {
                  const name = resp.id === UNASSIGNED_KEY ? "Sin asignar" : (getUserName(resp.id) || "Usuario");
                  return (
                    <Line
                      key={resp.id}
                      type="monotone"
                      dataKey={name}
                      stroke={getLineColor(resp.id)}
                      strokeWidth={2}
                      dot={{ fill: getLineColor(resp.id), strokeWidth: 0, r: 4 }}
                      activeDot={{ r: 6, strokeWidth: 0 }}
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Resumen Table with Expandable Rows */}
      <div className="bg-white border border-azul-surelia/15 rounded-xl shadow-sm overflow-hidden">
        <div className="p-4 border-b border-zinc-200 flex items-center justify-between">
          <Heading level={3} className="text-xl text-azul-surelia">
            Resumen por responsable
          </Heading>
          <Badge color="zinc">
            Período: {selectedMonth === "all" ? "Año completo" : monthNames[selectedMonth]} {selectedYear}
          </Badge>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHead className="bg-azul-surelia text-white">
              <TableRow>
                <TableHeader className="text-white w-8"></TableHeader>
                <TableHeader className="text-white">Responsable</TableHeader>
                <TableHeader className="text-white text-center">Clientes</TableHeader>
                <TableHeader className="text-white text-center">Contratos</TableHeader>
                <TableHeader className="text-white text-center">Fianzas</TableHeader>
                <TableHeader className="text-white text-right">Prima</TableHeader>
                <TableHeader className="text-white text-right">Comisión</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan="7" className="py-8 text-center text-azul-surelia/70">
                    Cargando datos…
                  </TableCell>
                </TableRow>
              ) : resumenPorResponsable.length === 0 ? (
                <TableRow>
                  <TableCell colSpan="7" className="py-8 text-center text-azul-surelia/70">
                    No hay datos para este periodo.
                  </TableCell>
                </TableRow>
              ) : (
                resumenPorResponsable.map((row, index) => {
                  const isUnassigned = row.id === UNASSIGNED_KEY;
                  const name = isUnassigned ? "Sin asignar" : (getUserName(row.id) || "Usuario");
                  const initials = isUnassigned ? "?" : (getInitials(row.id) || "?");
                  const isExpanded = expandedRows.has(row.id);
                  const monthlyData = getMonthlyDataForResponsable(row.id);

                  return (
                    <React.Fragment key={row.id}>
                      <TableRow
                        className={`transition-colors hover:bg-azul-surelia/5 cursor-pointer ${
                          index % 2 === 0 ? "bg-white" : "bg-zinc-50/50"
                        } ${isExpanded ? "bg-azul-surelia/5" : ""}`}
                        onClick={() => toggleRowExpanded(row.id)}
                      >
                        <TableCell className="py-3 w-8">
                          <ChevronDownIcon
                            className={`w-5 h-5 text-azul-surelia/60 transition-transform ${
                              isExpanded ? "rotate-0" : "-rotate-90"
                            }`}
                          />
                        </TableCell>
                        <TableCell className="py-3">
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold shrink-0 ${getAvatarColor(row.id)}`}
                            >
                              {initials}
                            </div>
                            <span className={`font-medium ${isUnassigned ? "text-gray-400 italic" : "text-zinc-900"}`}>
                              {name}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center py-3 text-zinc-700">{row.clientes}</TableCell>
                        <TableCell className="text-center py-3 text-zinc-700">{row.contratos}</TableCell>
                        <TableCell className="text-center py-3 text-zinc-700">
                          {Number.isInteger(row.fianzas) ? row.fianzas : row.fianzas.toFixed(1)}
                        </TableCell>
                        <TableCell className="text-right py-3 font-semibold text-zinc-900">
                          {formatCurrency(row.prima)}
                        </TableCell>
                        <TableCell className="text-right py-3 font-semibold text-zinc-900">
                          {formatCurrency(row.comision)}
                        </TableCell>
                      </TableRow>
                      {/* Expanded monthly breakdown rows */}
                      {isExpanded && monthlyData.map((monthData, monthIndex) => {
                        // Skip months with no activity
                        if (monthData.prima === 0 && monthData.count === 0) return null;
                        const isActiveRow = slideOverData?.responsableId === row.id && slideOverData?.monthIndex === monthIndex;
                        return (
                          <TableRow
                            key={`${row.id}-month-${monthIndex}`}
                            className={`cursor-pointer transition-colors ${
                              isActiveRow
                                ? "bg-azul-surelia/10 border-l-4 border-l-azul-surelia"
                                : "bg-zinc-50/80 hover:bg-azul-surelia/5"
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              // Toggle off if clicking the same row
                              if (slideOverData?.responsableId === row.id && slideOverData?.monthIndex === monthIndex) {
                                setSlideOverData(null);
                              } else {
                                setSlideOverData({ responsableId: row.id, monthIndex });
                              }
                            }}
                          >
                            <TableCell className="py-2 w-8"></TableCell>
                            <TableCell className={`py-2 pl-14 ${isActiveRow ? "pl-12" : ""}`}>
                              <span className={`text-sm ${isActiveRow ? "text-azul-surelia font-medium" : "text-azul-surelia/70 hover:text-azul-surelia hover:underline"}`}>
                                {monthNames[monthIndex].charAt(0).toUpperCase() + monthNames[monthIndex].slice(1)}
                              </span>
                            </TableCell>
                            <TableCell className="text-center py-2 text-zinc-600">
                              {monthData.clientes}
                            </TableCell>
                            <TableCell className="text-center py-2 text-zinc-600">
                              {monthData.contratos}
                            </TableCell>
                            <TableCell className="text-center py-2 text-zinc-600">
                              {Math.round(monthData.count)}
                            </TableCell>
                            <TableCell className="text-right py-2 text-zinc-700">
                              {formatCurrency(monthData.prima)}
                            </TableCell>
                            <TableCell className="text-right py-2 text-zinc-700">
                              {formatCurrency(monthData.comision)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Slide-over panel for fianzas list - rendered via portal (left side) */}
      {slideOverData && createPortal(
        <div className="fixed inset-y-0 left-0 z-[60] w-72 flex flex-col bg-white shadow-xl border-r border-azul-surelia/20">
          {/* Header */}
          <div className="bg-azul-surelia px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <h2 className="text-sm font-semibold text-white truncate">
                  {slideOverData.responsableId === UNASSIGNED_KEY
                    ? "Sin asignar"
                    : (getUserName(slideOverData.responsableId) || "Usuario")}
                </h2>
                <p className="text-xs text-white/80">
                  {monthNames[slideOverData.monthIndex].charAt(0).toUpperCase() +
                   monthNames[slideOverData.monthIndex].slice(1)} {selectedYear}
                </p>
              </div>
              <button
                onClick={() => setSlideOverData(null)}
                className="rounded-lg p-1.5 text-white/80 hover:bg-white/10 hover:text-white transition-colors ml-2"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-3">
            {(() => {
              const fianzas = getFianzasForResponsableMonth(
                slideOverData.responsableId,
                slideOverData.monthIndex
              );

              if (fianzas.length === 0) {
                return (
                  <div className="text-center py-8 text-zinc-500 text-sm">
                    No hay fianzas para este período.
                  </div>
                );
              }

              return (
                <div className="space-y-2">
                  {fianzas.map((fianza) => {
                    const prima = fianza.primaAmount || 0;
                    const comision = fianza.comisionMonto || 0;

                    return (
                      <div
                        key={fianza.id}
                        onClick={() => setSelectedFianza(fianza)}
                        className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 hover:border-azul-surelia/40 hover:bg-white cursor-pointer transition-all"
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-semibold text-azul-surelia truncate">
                              {formatNumeroFianza(fianza, "Sin número")}
                            </div>
                            <div className="text-xs text-zinc-500 truncate">
                              {fianza.clientName}
                            </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-1 text-xs">
                          <div>
                            <div className="text-zinc-500">Prima</div>
                            <div className="font-semibold text-zinc-900">{formatCurrency(prima)}</div>
                          </div>
                          <div>
                            <div className="text-zinc-500">Tarifa</div>
                            <div className="font-semibold text-zinc-800">
                              {fianza.fianzaTarifa !== null ? `${fianza.fianzaTarifa.toFixed(2)}%` : "N/A"}
                              {fianza.primaMinima && (
                                <span className="ml-1 text-[10px] text-amber-600 font-medium">Prima mín.</span>
                              )}
                            </div>
                          </div>
                          <div>
                            <div className="text-zinc-500">Comisión</div>
                            <div className="font-semibold text-azul-surelia">
                              {fianza.comisionMonto !== null ? formatCurrency(comision) : "N/A"}
                            </div>
                            {fianza.comisionPct !== null && (
                              <div className="text-[10px] text-azul-surelia/70">
                                {fianza.comisionPct}%
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {/* Summary */}
                  <div className="border-t border-zinc-200 pt-2 mt-3 space-y-1">
                    <div className="flex justify-between text-xs">
                      <span className="text-zinc-600">{fianzas.length} fianzas</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-zinc-600">Prima total</span>
                      <span className="font-semibold text-zinc-900">
                        {formatCurrency(fianzas.reduce((sum, f) => sum + (f.primaAmount || 0), 0))}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-zinc-600">Comisión total</span>
                      <span className="font-semibold text-azul-surelia">
                        {formatCurrency(fianzas.reduce((sum, f) => sum + (f.comisionMonto || 0), 0))}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>,
        document.body
      )}

      {/* FianzaView full-screen component - rendered via portal with higher z-index */}
      {selectedFianza && createPortal(
        <div className="relative z-[70]">
          <FianzaView
            isOpen={!!selectedFianza}
            onClose={() => setSelectedFianza(null)}
            fianza={selectedFianza}
            clientData={getClientDataForFianza(selectedFianza)}
            contratoData={getContractDataForFianza(selectedFianza)}
          />
        </div>,
        document.body
      )}
    </div>
  );
}
